import {
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Button,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SelectValue,
  Text,
} from "react-aria-components";
import { cn } from "@core/utils";
import { Icon } from "@core/assets";

/** One choice in a `Select`. `id` is the submitted value; `label` is presentation text. */
interface SelectOption {
  id: string;
  label: string;
}

interface SelectProps extends Omit<
  AriaSelectProps<SelectOption>,
  "className" | "children" | "items"
> {
  /** Accessible label. Always required; hide it visually with `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  options: readonly SelectOption[];
  /** Shown when nothing is selected. */
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  /** Stretch to fill the container's inline size (default) or hug its trigger. */
  fullWidth?: boolean;
  /**
   * `"md"` (default) is the form-field treatment every existing caller renders — untouched.
   * `"sm"` is the compact, button-like trigger Figma draws for an inline per-row choice (724:29580,
   * the competency panel's Obligatoire/Optionnel picker) — tighter padding, bold smaller text.
   */
  size?: "md" | "sm";
}

/**
 * Single-select primitive (own-the-code) on React Aria's `Select` — a real
 * listbox, not a native `<select>`, so the popover can be styled from tokens and stays
 * keyboard/screen-reader correct. The trigger mirrors `TextField`'s box treatment; the chevron
 * sits at the trailing edge.
 */
export function Select({
  label,
  hideLabel = false,
  options,
  placeholder,
  description,
  errorMessage,
  fullWidth = true,
  size = "md",
  ...props
}: SelectProps) {
  const invalid = Boolean(errorMessage) || props.isInvalid;

  return (
    <AriaSelect
      isInvalid={invalid}
      className={cn("flex flex-col gap-1.5", fullWidth && "w-full")}
      {...props}
    >
      <Label
        className={cn(
          "text-sm-medium text-(--text-secondary)",
          hideLabel && "sr-only"
        )}
      >
        {label}
        {props.isRequired ? (
          <span aria-hidden className="text-(--text-error)">
            {" "}
            *
          </span>
        ) : null}
      </Label>

      <Button
        className={cn(
          "flex w-full items-center gap-xs rounded-md bg-surface-field shadow-xs",
          size === "sm" ? "px-3.5 py-2" : "pl-3.5 pr-3.5 py-2.5",
          "border text-left transition-colors outline-none",
          "focus-visible:ring-2 focus-visible:ring-focus-ring",
          "disabled:cursor-not-allowed disabled:bg-surface-field-disabled",
          invalid
            ? "border-(--border-field-error)"
            : "border-(--border-field) focus-visible:border-(--focus-ring)"
        )}
      >
        <SelectValue
          className={cn(
            "min-w-0 flex-1 truncate data-placeholder:text-(--text-placeholder)",
            size === "sm"
              ? "text-sm-semibold text-(--text-secondary)"
              : "text-md-regular text-(--text-primary)"
          )}
        >
          {({ isPlaceholder, selectedText }) =>
            isPlaceholder ? (placeholder ?? label) : selectedText
          }
        </SelectValue>
        <Icon
          name="chevron-down"
          size={20}
          className="shrink-0 text-(--text-tertiary)"
        />
      </Button>

      {description ? (
        <Text
          slot="description"
          className="text-sm-regular text-(--text-tertiary)"
        >
          {description}
        </Text>
      ) : null}

      <FieldError className="text-sm-regular text-(--text-error)">
        {errorMessage}
      </FieldError>

      <Popover className="w-(--trigger-width) overflow-auto rounded-md border border-(--border-default) bg-surface-card py-1 shadow-lg">
        <ListBox items={options} className="outline-none">
          {(option) => (
            <ListBoxItem
              id={option.id}
              textValue={option.label}
              className={cn(
                "flex cursor-pointer items-center justify-between gap-xs px-3.5 py-2.5",
                "text-md-regular text-(--text-primary) outline-none",
                "focus:bg-brand-50 selected:bg-brand-50 selected:text-(--text-link)"
              )}
            >
              {({ isSelected }) => (
                <>
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                  {isSelected ? (
                    <Icon
                      name="check"
                      size={16}
                      className="shrink-0 text-(--text-link)"
                    />
                  ) : null}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

export type { SelectProps, SelectOption };
